import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Mail, Phone, MapPin, Send, Clock, Globe } from 'lucide-react';
import { toast } from 'sonner';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
const handleSubmit = (e) => {
  e.preventDefault();
  
  // Validation
  if (!formData.name || !formData.email || !formData.subject || !formData.message) {
    toast.error('Please fill in all fields');
    return;
  }

  const templateParams = {
    from_name: formData.name,
    from_email: formData.email,
    subject: formData.subject,
    message: formData.message,
  };

  emailjs
    .send(
      'service_209amzm',
      'template_lf3nw7f',
      templateParams,
      'onANKtU6ZYfyXj169'
    )
    .then(() => {
      toast.success('Message sent successfully! We\'ll get back to you soon.');

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
      });
    })
    .catch((error) => {
      console.error(error);
      toast.error('Failed to send message. Please try again.');
    });
};

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email Us',
      content: 'support@quantumfinanceguide.ltd',
      description: 'Get response within 24 hours',
    },
    {
      icon: Phone,
      title: 'Call Us',
      content: 'On Demand',
      description: 'Monday to Friday, 9AM - 6PM EST',
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      content: 'New York',
      description: 'Our headquarters',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Get In Touch
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {contactInfo.map((info, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                        <info.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">
                        {info.title}
                      </h3>
                      <p className="text-lg text-gray-700 font-semibold mb-2">
                        {info.content}
                      </p>
                      <p className="text-gray-600">
                        {info.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Contact Form */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              {/* Form */}
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Send Us a Message
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Fill out the form below and our team will get back to you within 24 hours.
                </p>

                <Card className="bg-white shadow-2xl border-2 border-gray-100">
                  <CardContent className="p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Name */}
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-lg font-semibold text-[#0B2A4A]">
                          Your Name
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                          required
                        />
                      </div>

                      {/* Email */}
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-lg font-semibold text-[#0B2A4A]">
                          Email Address
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john.doe@example.com"
                          className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                          required
                        />
                      </div>

                      {/* Subject */}
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-lg font-semibold text-[#0B2A4A]">
                          Subject
                        </Label>
                        <Input
                          id="subject"
                          name="subject"
                          type="text"
                          value={formData.subject}
                          onChange={handleChange}
                          placeholder="How can we help?"
                          className="h-12 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors"
                          required
                        />
                      </div>

                      {/* Message */}
                      <div className="space-y-2">
                        <Label htmlFor="message" className="text-lg font-semibold text-[#0B2A4A]">
                          Message
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder="Tell us what you'd like to know..."
                          className="min-h-40 text-lg border-2 border-gray-200 focus:border-[#FFD700] transition-colors resize-none"
                          required
                        />
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        className="w-full bg-[#FFD700] text-[#0B2A4A] hover:bg-[#FFD700]/90 font-bold py-6 text-lg rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 group"
                      >
                        <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                        Send Message
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              {/* Additional Info */}
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Office Information
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Visit our headquarters or reach out through any of our communication channels.
                </p>

                <div className="space-y-6 mb-12">
                  <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#0B2A4A]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-2">Business Hours</h3>
                      <p className="text-gray-600">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                      <p className="text-gray-600">Saturday: 10:00 AM - 4:00 PM EST</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                    <div className="w-12 h-12 bg-[#FFD700] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Globe className="w-6 h-6 text-[#0B2A4A]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-2">Global Presence</h3>
                      <p className="text-gray-600">Headquarters: New York, USA</p>
                      <p className="text-gray-600">Serving customers worldwide</p>
                    </div>
                  </div>
                </div>

                {/* Map Placeholder */}
                <div className="relative h-80 bg-gray-200 rounded-2xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1758862527435-1b623cbd4206?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBpbm5vdmF0aW9ufGVufDB8fHx8MTc3MzczNTcwMHww&ixlib=rb-4.1.0&q=85"
                    alt="Office Location"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A]/60 to-transparent flex items-end p-6">
                    <div>
                      <h3 className="text-white font-bold text-xl mb-1">New York Office</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* FAQ Section */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Quick answers to common questions
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700]">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">How long does card approval take?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Most applications are reviewed within 2-3 business days. You'll receive an email notification once your application status is updated.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700]">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">What are the card fees?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Card fees vary by tier. Gold cards have no annual fee, Platinum cards have a $95 annual fee (waived first year), and Elite cards have a $450 annual fee with premium benefits.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700]">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Is my information secure?</h3>
                <p className="text-gray-600 leading-relaxed">
                  Yes, we use bank-level encryption and security protocols to protect all your personal and financial information.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
};

export default Contact;
